import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import { ROUTES } from "@/app/routes";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { IUserRoleClinicList } from "@/api_framework/api_modals/UserRoles";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { setMappedCityId, setMappedClinicData } from "@/app_redux/reducers/slices/ui_states_slice";
import SideGridImage from "@assets/images/SelectCityAndClinicModalImage.png";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { format } from "date-fns";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";

interface IUserCityAndClinicFormFields {
  city_id: string;
  clinic_id: string;
  clinic_name: string;
}
interface IUserClinicMapParams {
  isCrossVisible?: boolean;
  setIsVisibleMappedClinic?: (isVisible: boolean) => void;
  message?: string;
  isCartEmrCreate?: boolean;
  handelCartEmrCreate?: (city: any, clinic: any) => void;
  setCarteditDetails?: (e: any) => void;
  carteditDetails?: any;
  skipGlobalMappingUpdate?: boolean;
  /**
   * Only let the user continue with a clinic where the customer actually has one of
   * today's bookings, and prefill city + clinic from that booking. Opt-in (EMR-tab
   * "Create EMR"); every other caller keeps the free choice it has today.
   */
  enforceBookingClinic?: boolean;
}

const MappedClinicAndCityPage: React.FC<IUserClinicMapParams> = ({
  isCrossVisible,
  setIsVisibleMappedClinic,
  message,
  handelCartEmrCreate,
  isCartEmrCreate,
  carteditDetails,
  setCarteditDetails,
  skipGlobalMappingUpdate = false,
  enforceBookingClinic = false,
}) => {
  const authUserDisplayName = useAppSelector((state) => state?.auth?.user?.displayName);
  const { clinics, cities, mappedCityId, mappedClinicId, mappedClinicName } = useAppSelector(
    (state) => state.root.UIStates,
  );
  const [isModalOpen, setIsModalOpen] = useState(true); // Modal visibility state

  const { routeState } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);
  const { customerBookings } = routeState.state as ISlotsState;
  const { getRouteKey } = useRouter();
  const { customerDetails } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState
    .state as ICustomerProfileState;

  const dispatch = useAppDispatch();
  const fetchCities = useOrgCitiesListAPI("UI");
  const fetchClinics = useStaffClinicListAPI("UI");
  const configuration = useConfiguration();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.MAPPED_CLINIC_POPUP);

  const formHook = useForm<IUserCityAndClinicFormFields>({
    mode: "onChange",
  });

  /* Select Items Section Starts */
  const citySelectItems = useSelectMenuReducer(cities, "name", "id");
  const clinicSelectItems = useSelectMenuReducer(clinics, "display_name", "id");
  const navigate = useNavigate();
  /* Select Items Section Ends */

  useEffect(() => {
    fetchCities({
      withClinics: false,
    });
  }, []);

  useEffect(() => {
    if (formHook.getValues("city_id")) {
      fetchClinics({
        cityIds: [formHook.getValues("city_id")],
        withProviders: false,
      });
      !isCartEmrCreate && formHook.setValue("clinic_id", null);
      !isCartEmrCreate && formHook.setValue("clinic_name", null);
    }
  }, [formHook.watch("city_id")]);

  const onSubmit = async (data: IUserCityAndClinicFormFields) => {
    await Promise.race([
      logVMSClick(VMS_CLICK_EVENTS.MAPPED_CLINIC_SAVE_CLICK, isCartEmrCreate ? "Continue" : "Save", {
        clinic_id: data.clinic_id,
        clinic_name: data.clinic_name,
        previous_clinic_id: mappedClinicId,
        previous_clinic_name: mappedClinicName,
      }),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]);
    if (!skipGlobalMappingUpdate) {
      dispatch(
        setMappedClinicData({
          id: data.clinic_id,
          name: data.clinic_name,
        }),
      );
      dispatch(setMappedCityId(data.city_id));

      localStorage.setItem(configuration.localStorage.CITY.key, data.city_id);
      localStorage.setItem(configuration.localStorage.CLINIC.key, data.clinic_id);
      localStorage.setItem(configuration.localStorage.CLINIC_NAME.key, data.clinic_name);

      toast.success("Clinic Mapped");
    }
    if (isCrossVisible) {
      setIsModalOpen(false);
      !isCartEmrCreate && window.location.reload();
    }
    if (isCartEmrCreate) {
      handelCartEmrCreate(data.clinic_id, data.clinic_name);
      return;
    }

    navigate(getRouteKey("SEARCH_CUSTOMER", "url"));
  };

  // Local date, not toISOString(): that is UTC, which in IST is still yesterday until 05:30.
  const today = format(new Date(), "yyyy-MM-dd");
  const isBookingPresent = customerBookings?.data?.filter(
    (b) => b.booking_start_date === today && !["Cancelled", "Failed"].includes(b.booking_status),
  );

  /**
   * Prefill city + clinic from today's booking so the doctor doesn't have to hunt for it.
   * Bookings carry only the clinic, so the city is resolved from the staff's assigned
   * cities (each lists its clinics) — which also skips clinics the staff can't access.
   * Runs once; after that the doctor's own pick is left alone.
   */
  const hasPrefilledFromBooking = useRef(false);
  useEffect(() => {
    if (!enforceBookingClinic || hasPrefilledFromBooking.current) return;
    // The slice types these as plain cities, but they are assigned_cities_clinics (see useOrgCitiesListAPI).
    const assignedCities = cities as unknown as IUserRoleClinicList[];
    if (!isBookingPresent?.length || !assignedCities?.length) return;

    for (const booking of isBookingPresent) {
      const city = assignedCities.find((c) => c?.clinics?.some((cl) => cl?.id === booking?.clinic_id));
      if (!city) continue;
      const clinic = city.clinics.find((cl) => cl?.id === booking.clinic_id);
      formHook.setValue("city_id", city.id, { shouldValidate: true });
      formHook.setValue("clinic_id", booking.clinic_id, { shouldValidate: true });
      formHook.setValue("clinic_name", clinic?.display_name ?? booking?.clinic_details?.display_name);
      hasPrefilledFromBooking.current = true;
      return;
    }
  }, [enforceBookingClinic, isBookingPresent, cities]);

  const cancelButtonRef = useRef(null);
  const isDismissedRef = useRef(false);
  const handleAfterLeave = () => {
    if (!isDismissedRef.current) return;
    isDismissedRef.current = false;
    setIsVisibleMappedClinic?.(false);
    setCarteditDetails?.(null);
  };
  const handleClose = () => {
    //
  };

  const selectedClinicId = formHook.watch("clinic_id");
  const selectedClinicHasBooking = isBookingPresent?.some((b) => b?.clinic_id === selectedClinicId);

  /**
   * The customer has appointments today but none in the clinic picked here, so the
   * EMR would be filed against a clinic the customer never visited — hold Continue
   * until one of those clinics is selected. With no bookings today there is nothing
   * to match against (the blue note), so the choice stays open rather than locking
   * the user in a modal with no way forward.
   */
  const isContinueDisabled = enforceBookingClinic && isBookingPresent?.length > 0 && !selectedClinicHasBooking;

  return (
    <Transition.Root show={isModalOpen} as={Fragment} appear afterLeave={handleAfterLeave}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="z-10 fixed inset-0 w-screen overflow-y-auto">
          <div className="flex justify-center items-center p-4 min-h-[100dvh] text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4
                w-full max-w-[95vw] sm:max-w-3xl sm:w-full sm:p-6`}
              >
                <div className={`flex ${isCartEmrCreate ? "justify-between" : "justify-end"} items-start gap-3 mb-2`}>
                  {isCartEmrCreate && (
                    <div
                      className={clsx(
                        "mt-3 p-3 rounded-lg border shadow-sm flex-1",
                        isBookingPresent?.length > 0
                          ? "bg-yellow-50 border-yellow-300 text-yellow-800"
                          : "bg-blue-50 border-blue-300 text-blue-800",
                      )}
                    >
                      <p className="text-xs sm:text-sm font-medium break-words leading-relaxed">
                        {isBookingPresent?.length > 0
                          ? `⚠️ Caution: ${customerDetails?.name} already has an appointment in ${isBookingPresent[0]?.clinic_details?.display_name} ${
                              isBookingPresent?.length > 1
                                ? `and ${isBookingPresent[1]?.clinic_details?.display_name}`
                                : ""
                            } ${
                              isBookingPresent?.length > 2
                                ? `and ${isBookingPresent[2]?.clinic_details?.display_name}`
                                : ""
                            } ${
                              isBookingPresent?.length > 3
                                ? `and ${isBookingPresent[3]?.clinic_details?.display_name}`
                                : ""
                            }   clinic. Please change the clinic to continue.`
                          : "ℹ️ Please check your mapped clinic. No bookings were found for the user in this clinic."}
                      </p>
                    </div>
                  )}

                  {isCrossVisible && (
                    <XMarkIcon
                      className="h-5 w-5 cursor-pointer font-bold shrink-0"
                      onClick={() => {
                        isDismissedRef.current = true;
                        setIsModalOpen(false);
                      }}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 w-full h-full gap-4 lg:gap-2">
                  <div className="col-span-1 lg:col-span-3 min-w-0">
                    <div className="flex flex-col justify-center items-start gap-y-3 w-full h-full">
                      <p className="text-gray-600 text-left text-lg sm:text-xl">
                        Hi, <strong>{authUserDisplayName}</strong> 👋
                      </p>
                      <p className="text-gray-500 text-base sm:text-lg">
                        You need to select your designated <strong>City</strong> and <strong>Clinic</strong> to use
                        VMS.{" "}
                      </p>
                      <div className="mt-3 sm:mt-4 w-full">
                        <form
                          className="flex flex-col lg:flex-row lg:items-end gap-3 w-full"
                          onSubmit={formHook.handleSubmit(onSubmit)}
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full min-w-0 lg:flex-1">
                            <SearchableSelectMenu
                              errorMessages={[{ message: "City is required", type: "required" }]}
                              showDropdownIcon
                              listZIndex="z-[80]"
                              listBoxClassName="w-full max-h-[40dvh] sm:max-h-60"
                              placeholder="Select City"
                              fieldError={formHook.formState.errors.city_id}
                              register={formHook.register("city_id", { required: true })}
                              showTypedErrors
                              selectItems={citySelectItems}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("city_id", item.id);
                                }
                              }}
                              defaultSelected={citySelectItems?.filter((c) => c.id === formHook.watch("city_id"))[0]}
                            />
                            <SearchableSelectMenu
                              errorMessages={[{ message: "Clinic is required", type: "required" }]}
                              showDropdownIcon
                              listZIndex="z-[80]"
                              listBoxClassName="w-full max-h-[40dvh] sm:max-h-60"
                              placeholder="Select Clinic"
                              fieldError={formHook.formState.errors.clinic_id}
                              register={formHook.register("clinic_id", { required: true })}
                              showTypedErrors
                              selectItems={clinicSelectItems}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue("clinic_id", item.id);
                                  formHook.setValue("clinic_name", item.title);
                                  localStorage.setItem("clinic_snp", item.id);
                                  localStorage.setItem("clinic_bad", item.id);
                                  localStorage.setItem("order_item", "0");
                                }
                              }}
                              defaultSelected={
                                clinicSelectItems?.filter((c) => c.id === formHook.watch("clinic_id"))[0]
                              }
                            />
                          </div>
                          <div className="w-full sm:w-auto sm:self-start lg:self-auto lg:shrink-0">
                            <input
                              type="submit"
                              disabled={isContinueDisabled}
                              value={`${isCartEmrCreate ? "Continue" : "Save"}`}
                              className={clsx(
                                "shadow-sm w-full sm:w-auto px-8 py-2 rounded-md font-semibold text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                                isContinueDisabled
                                  ? "bg-gray-400 text-white cursor-not-allowed"
                                  : "bg-blue-900 hover:bg-blue-500 cursor-pointer text-white focus-visible:outline-indigo-600",
                              )}
                            />
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="hidden lg:flex lg:col-span-2 items-end min-w-0">
                    <img
                      src={SideGridImage}
                      alt="side_grid_image"
                      width={648}
                      height={691}
                      decoding="async"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MappedClinicAndCityPage;
